import classNames from 'classnames';
import {
  ChevronRight,
  Copy,
  Earth,
  Eye,
  Mail as IconEmail,
  Facebook as IconFacebook,
  MessageSquare,
  Minus,
  Plus,
  Share,
  SquarePen,
} from 'lucide-react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  EmailShareButton,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share';
import { toast } from 'react-toastify';
import { compose } from 'redux';
import { ExternalLink, H3, IconTwitterX, IconWhatsApp } from '../../components';

import { facebookIcon, instagramIcon, tiktokIcon } from '../PageBuilder/Primitives/Link/Icons';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { richText } from '../../util/richText';
import {
  REVIEW_TYPE_OF_PROVIDER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';

import {
  AvatarLarge,
  H2,
  H4,
  IconArrowHead,
  LayoutSingleColumn,
  Modal,
  NamedLink,
  Page,
  PublicListingCard,
  ResponsiveImage,
  ReviewRating,
  Reviews,
  NamedRedirect,
} from '../../components';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

// import { listingImages } from '../ListingPage/ListingPage.shared';

import SectionGallery from '../ListingPage/SectionGallery';
import ProviderContact from '../ProviderContact/ProviderContact';
import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import ProviderSocialShareCallout from '../SocialSharePage/ProviderSocialShareCallout/ProviderSocialShareCallout';

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const TOPBAR_HEIGHT_MOBILE = 106;
const TOPBAR_HEIGHT_DESKTOP = 86;
const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {isCurrentUser ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const ProfileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);

  return (
    <div id="reviews" className={css.profileReviews}>
      <H4 as="h2" className={css.profileReviewsTitle}>
        <FormattedMessage id="ProfilePage.reviewsHeading" />
      </H4>
      <div className={css.profileReviewsSubtitle}>
        <FormattedMessage
          id="ProfilePage.reviewsCount"
          values={{ count: reviewsOfProvider.length }}
        />
      </div>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <div>
        <Reviews reviews={reviewsOfProvider} className={css.profileReviewsContainer} />
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, ...fieldProps } = customFieldProps;
        const isLanguagesEnum = customFieldProps.key === 'languages';
        const isTagline = customFieldProps.key === 'tagline';
        if (isLanguagesEnum || isTagline) {
          // Display will be done separately
          return null;
        }
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    publicData,
    metadata,
    isCurrentUser,
    userFieldConfig,
    intl,
    user,
    onShareClick,
  } = props;

  const [openInquiryModal, setOpenInquiryModal] = useState(false);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);
  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const tagline = publicData?.tagline ? publicData.tagline : null;
  const hasTagline = !!tagline;

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  // The hidden inquiry listing is created automatically for the provider.
  // Currently triggered by Zapier when a provider creates their first
  // real listing.
  const hiddenInquiryListing = listings.filter(
    l => l.attributes.publicData.listingType === 'hidden_provider_inquiry'
  );
  const reviewAverage = metadata?.reviewAverage ? metadata?.reviewAverage : 0;
  const reviewCount = metadata?.reviewCount ? metadata?.reviewCount : 0;

  const languages = publicData?.languages
    ? publicData.languages.flatMap(language => language.label).join(', ')
    : '';

  const instagram = publicData?.external?.instagram
    ? `https://instagram.com/${publicData.external.instagram}`
    : null;

  const facebook = publicData?.external?.facebook
    ? `https://facebook.com/${publicData.external.facebook}`
    : null;

  const tiktok = publicData?.external?.tiktok
    ? `https://tiktok.com/@${publicData.external.tiktok}`
    : null;

  return (
    <div className={css.root}>
      <div className={css.profileSummary}>
        {user && <SectionGallery listing={user} variantPrefix={'listing-card'} isProfile={true} />}
        <div className={css.mobileIcons}>
          <button
            onClick={e => {
              onShareClick(e);
            }}
            title={intl.formatMessage({ id: 'ProfilePage.heading.share' })}
          >
            <Share />
          </button>
          {!isCurrentUser && hiddenInquiryListing.length === 1 && (
            <button
              onClick={e => {
                setOpenInquiryModal(true);
              }}
              title={intl.formatMessage(
                { id: 'ProfilePage.heading.message.title' },
                { name: displayName }
              )}
            >
              <MessageSquare />
            </button>
          )}
          {isCurrentUser && (
            <NamedLink
              className={css.editProfileButton}
              name="EditProfilePage"
              params={{ type: 'edit', tab: 'name' }}
              title={intl.formatMessage({ id: 'ProfilePage.heading.edit.title' })}
            >
              <SquarePen />
            </NamedLink>
          )}
        </div>
        <div className={css.headerPanel}>
          <div className={css.headerLeft}>
            <AvatarLarge className={css.avatar} user={user} disableProfileLink />
            <h1>{displayName}</h1>
          </div>
          <div className={css.headerCenter}>
            <h1>{displayName}</h1>
            <p>{tagline}</p>
          </div>
          <div className={css.headerRight}>
            <button
              onClick={e => {
                onShareClick(e);
              }}
              title={intl.formatMessage({ id: 'ProfilePage.heading.share.title' })}
            >
              <Share />
              <FormattedMessage id="ProfilePage.heading.share" />
            </button>
            {!isCurrentUser && hiddenInquiryListing.length === 1 && (
              <button
                onClick={e => {
                  setOpenInquiryModal(true);
                }}
                title={intl.formatMessage(
                  { id: 'ProfilePage.heading.message.title' },
                  { name: displayName }
                )}
              >
                <MessageSquare />
                <FormattedMessage id="ProfilePage.heading.message" />
              </button>
            )}
            {isCurrentUser && (
              <NamedLink
                className={css.editProfileButton}
                name="EditProfilePage"
                params={{ type: 'edit', tab: 'name' }}
                title={intl.formatMessage({ id: 'ProfilePage.heading.edit.title' })}
              >
                <SquarePen />
                <FormattedMessage id="ProfilePage.heading.edit" />
              </NamedLink>
            )}
          </div>
        </div>
      </div>
      <div className={css.profileContainer}>
        <div className={css.about}>
          <div
            className={css.reviewContainer}
            onClick={e => {
              e.preventDefault();

              const margin = 6;

              const element = document.getElementById('reviews');
              const topbarOffset = isMobileLayout ? TOPBAR_HEIGHT_MOBILE : TOPBAR_HEIGHT_DESKTOP;
              const position = element.getBoundingClientRect().top;
              const offsetPosition = position + window.scrollY - topbarOffset - margin;

              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
              });
            }}
          >
            <ReviewRating
              rating={reviewAverage ? reviewAverage : 0}
              rootClassName={css.reviewRatingRoot}
              reviewCirclesClassName={css.reviewCircles}
              reviewRatingClassName={css.reviewRatingClassName}
            />
            <div className={css.reviewDivider}></div>
            <div className={css.reviewCountSummary}>
              <div className={css.reviewCount}>{reviewCount}</div>
              <div className={css.reviewCountLabel}>
                <FormattedMessage id="ProfilePage.reviews" values={{ count: reviewCount }} />
              </div>
            </div>
            <ChevronRight className={css.reviewChevron} />
          </div>

          {(!isMobileLayout || (isMobileLayout && isAboutExpanded)) && (
            <div className={css.reviewText}>
              <h2>
                <FormattedMessage id="ProfilePage.aboutHeading" />
              </h2>
              {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}
              <div className={css.external}>
                {instagram && <ExternalLink href={instagram}>{instagramIcon()}</ExternalLink>}
                {facebook && <ExternalLink href={facebook}>{facebookIcon()}</ExternalLink>}
                {tiktok && <ExternalLink href={tiktok}>{tiktokIcon()}</ExternalLink>}
              </div>
            </div>
          )}
          {(!isMobileLayout || (isMobileLayout && isAboutExpanded)) && (
            <div className={css.langsExternal}>
              <div>
                <div className={css.hr}></div>
                <div className={css.languages}>
                  <Earth className={css.languagesIcon} />
                  <FormattedMessage id="ProfilePage.languages" className={css.languagesLabel} />
                  <span className={css.languagesList}>{languages}</span>
                </div>
              </div>
            </div>
          )}
          {isMobileLayout && (
            <div
              className={css.aboutReadMore}
              onClick={e => {
                setIsAboutExpanded(!isAboutExpanded);
              }}
            >
              {!isAboutExpanded ? (
                <FormattedMessage id="ProfilePage.readMore" />
              ) : (
                <FormattedMessage id="ProfilePage.readLess" />
              )}
              <div className={css.readMoreIconContainer}>
                {!isAboutExpanded ? <Plus /> : <Minus />}
              </div>
            </div>
          )}
        </div>
        <div>
          {/* <CustomUserFields
        publicData={publicData}
        metadata={metadata}
        userFieldConfig={userFieldConfig}
        intl={intl}
      /> */}

          {hasListings ? (
            <div className={listingsContainerClasses}>
              <H4 as="h2" className={css.listingsTitle}>
                <FormattedMessage id="ProfilePage.listingsTitle" />
              </H4>
              <ul className={css.listings}>
                {listings
                  .filter(
                    // Don't show the hidden inquiry listing.
                    l => l.attributes.publicData.listingType !== 'hidden_provider_inquiry'
                  )
                  .map(l => (
                    <li className={css.listing} key={l.id.uuid}>
                      <PublicListingCard listing={l} showAuthorInfo={false} />
                    </li>
                  ))}
              </ul>
              {hiddenInquiryListing.length === 1 && (
                // Only providers should have the hidden inquiry listing
                <ProviderContact
                  openInquiryModal={openInquiryModal}
                  onCloseInquiryModal={e => setOpenInquiryModal(false)}
                  params={{
                    id: hiddenInquiryListing[0].id.uuid,
                  }}
                />
              )}
            </div>
          ) : null}

          <ProfileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
        </div>
      </div>
    </div>
  );
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const {
    scrollingDisabled,
    currentUser,
    userShowError,
    user,
    onManageDisableScrolling,
    ...rest
  } = props;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const location = useLocation();
  const [showShareModal, setShowShareModal] = useState(false);
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const firstImage =
    profileUser?.attributes?.profile?.publicData?.media &&
    Array.isArray(profileUser?.attributes?.profile?.publicData?.media) &&
    profileUser.attributes.profile.publicData.media.length > 0
      ? profileUser.attributes.profile.publicData.media[0]
      : null;

  const marketplaceName = config.marketplaceName;

  const schemaTitleVars = { name: displayName, marketplaceName: marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const tagline = publicData?.tagline ? publicData.tagline : '';

  const schemaImages = profileImages(
    profileUser,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const facebookImages = profileImages(profileUser, 'facebook');
  const twitterImages = profileImages(profileUser, 'twitter');

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  let query = useQuery();
  const isPreview = query.get('isPreview') === 'true';
  const [showReviewModal, setShowReviewModal] = useState(isPreview);

  const variants = firstImage?.attributes?.variants
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith('listing-card'))
    : [];
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      author={displayName}
      description={tagline}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        description: tagline,
        image: schemaImages,
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        topbar={
          <>
            <TopbarContainer />
          </>
        }
        footer={<FooterContainer />}
      >
        {/* {isCurrentUser && !isPreview ? (
          <ActionBarMaybe className={css.actionBarForProductLayout} isCurrentUser={isCurrentUser} />
        ) : null} */}

        <Modal
          id={'profile-page-review'}
          scrollLayerClassName={css.reviewModalScrollLayer}
          containerClassName={css.reviewModalContainer}
          contentClassName={css.reviewModalContent}
          isOpen={showReviewModal}
          onClose={() => {
            setShowReviewModal(false);
          }}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div>
            <div className={css.reviewModalTitle}>
              <FormattedMessage id="ProfilePage.review.modal.title" />
            </div>
            <div className={css.reviewModalImageContainer}>
              <ResponsiveImage
                rootClassName={css.rootForImage}
                alt={'profile media'}
                image={firstImage}
                variants={variants}
              />
              <div className={css.reviewModalImageOverlay}></div>
            </div>
            <div className={css.reviewModalSubtitle}>
              <FormattedMessage id="ProfilePage.review.modal.subTitle" />
            </div>
            <p className={css.reviewModalText}>
              <FormattedMessage id="ProfilePage.review.modal.content" />
            </p>
            <div className={css.reviewModalCopy}>
              <FormattedMessage id="ProfilePage.review.modal.copy" />
              <div className={css.shareCopyToClipboardContainer}>
                <input value={productURL} disabled={true} />
                <CopyToClipboard
                  text={productURL}
                  onCopy={() => {
                    toast('Copied to clipboard');
                  }}
                >
                  <button>
                    <Copy />
                  </button>
                </CopyToClipboard>
              </div>
            </div>
          </div>
          <div className={css.modalButtonBar}>
            <NamedLink
              className={css.backLink}
              name="EditProfilePage"
              params={{ type: 'new', tab: 'media' }}
            >
              <IconArrowHead direction="left" size="big" rootClassName={css.arrowIcon} />
              <FormattedMessage id="ProfilePage.review.modal.backButton" />
            </NamedLink>
            <button
              className={css.profileLink}
              onClick={e => {
                e.preventDefault();
                setShowReviewModal(!showReviewModal);
              }}
            >
              <FormattedMessage id="ProfilePage.reviewProfile.button" />
              <Eye className={css.viewProfileIcon} />
            </button>
          </div>
        </Modal>

        <Modal
          id={'listing-page-share'}
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
          }}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <FormattedMessage
            id="ProfilePage.share.modal.title"
            values={{ profileTitle: displayName }}
          />
          <div className={css.shareNetworkContainer}>
            <div className={css.shareNetwork}>
              <FacebookShareButton url={productURL} className={css.shareButton}>
                <IconFacebook className={css.shareIcon} />
              </FacebookShareButton>
            </div>
            <div className={css.shareNetwork}>
              <TwitterShareButton url={productURL} title={schemaTitle} className={css.shareButton}>
                <IconTwitterX className={css.shareIconCustom} />
              </TwitterShareButton>
            </div>
            <div className={css.shareNetwork}>
              <EmailShareButton
                url={productURL}
                subject={schemaTitle}
                body={''}
                className={css.shareButton}
              >
                <IconEmail className={css.shareIcon} />
              </EmailShareButton>
            </div>
            <div className={css.shareNetwork}>
              <WhatsappShareButton
                url={productURL}
                title={schemaTitle}
                separator=":: "
                className={css.shareButton}
              >
                <IconWhatsApp className={css.shareIconCustom} />
              </WhatsappShareButton>
            </div>
            <div className={css.shareCopyToClipboardContainer}>
              <input value={productURL} disabled={true} />
              <CopyToClipboard
                text={productURL}
                onCopy={() => {
                  toast('Copied to clipboard');
                }}
              >
                <button>
                  <Copy />
                </button>
              </CopyToClipboard>
            </div>
            {isCurrentUser && (
              <div className={css.providerSocialShareWrapper}>
                <ProviderSocialShareCallout
                  profileOrListingId={currentUser?.id?.uuid}
                  intl={intl}
                />
              </div>
            )}
          </div>
        </Modal>

        {isPreview && !showReviewModal && (
          <div className={css.stickyButtons}>
            <NamedLink
              className={css.backLink}
              name="EditProfilePage"
              params={{ type: 'new', tab: 'media' }}
            >
              <IconArrowHead direction="left" size="big" rootClassName={css.arrowIcon} />
              <FormattedMessage id="ProfilePage.review.modal.backButton" />
            </NamedLink>
            <NamedLink className={css.completeLink} name="ListingOnboardingPage">
              <FormattedMessage id="ProfilePage.review.modal.completeButton" />
              <IconArrowHead direction="right" size="big" rootClassName={css.completeArrowIcon} />
            </NamedLink>
          </div>
        )}

        <MainContent
          bio={bio}
          displayName={!isPreview ? displayName : currentUser?.attributes?.profile?.displayName}
          userShowError={userShowError}
          publicData={!isPreview ? publicData : currentUser?.attributes?.profile?.publicData}
          metadata={!isPreview ? metadata : currentUser?.attributes?.profile?.metadata}
          userFieldConfig={userFields}
          intl={intl}
          user={user}
          onShareClick={e => setShowShareModal(true)}
          isCurrentUser={isCurrentUser}
          // onMessageClick={}
          {...rest}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,
  onManageDisableScrolling: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProfilePageComponent);

export default ProfilePage;

/**
 * Filter profile media to only images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} profile
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const profileImages = (user, variantName) => {
  const media = user?.attributes?.profile?.publicData?.media;
  return (Array.isArray(media) ? media : [])
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      if (image.ik.videoCodec && !variant.url.includes('.mp4/ik-thumbnail.jpg')) {
        // For videos we need to add the thumbnail prefix for ImageKit
        variant.url = variant.url.replace('.mp4', '.mp4/ik-thumbnail.jpg');
      }
      return variant || size;
    })
    .filter(variant => variant != null);
};
